import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { AccommodationOption, OfferOptions } from '../queries';
import { formatMoney } from '../money';
import { MOBILE_TAP_TARGET } from '../bookingMobile';
import { hasSeatGeometry } from '../offerOptionsRender';

interface Props {
  options: OfferOptions;
  passengers: number;
  /** La calidad de la oferta: los asientos elegibles son de ella (o de la elegida). */
  offerClassCode?: string;
  accommodationIds: string[];
  onAccommodationIdsChange: (ids: string[]) => void;
  ticketTypeId: string;
  onTicketTypeChange: (id: string) => void;
}

/**
 * El mapa de la unidad, dibujado con la geometría que publica el proveedor
 * (fila/columna/piso). Elegir asiento es OPCIONAL: sin elección el hold
 * auto-asigna los primeros libres de la calidad — por eso acá no hay ningún
 * "requerido", solo la regla de todo-o-nada (0 asientos o uno por pasajero),
 * que valida el modal.
 *
 * El selector de calidad va junto al mapa porque es donde la decisión ocurre:
 * el pasajero ve el piso de Cama medio vacío y cambia. El precio de la calidad
 * elegida se cotiza contra la de la oferta (el modal hace esa cuenta).
 */
export function BusSeatPicker({
  options,
  passengers,
  offerClassCode,
  accommodationIds,
  onAccommodationIdsChange,
  ticketTypeId,
  onTicketTypeChange,
}: Props) {
  const { t } = useTranslation();

  const seats = useMemo(
    () => options.accommodations.filter(hasSeatGeometry),
    [options.accommodations],
  );
  /** Pisos presentes, ordenados: un común tiene 1, un doble piso 1 y 2. */
  const decks = useMemo(
    () => [...new Set(seats.map((seat) => seat.deck ?? 1))].sort((a, b) => a - b),
    [seats],
  );

  if (seats.length === 0) return null;

  const toggleSeat = (seat: AccommodationOption) => {
    if (accommodationIds.includes(seat.id)) {
      onAccommodationIdsChange(accommodationIds.filter((id) => id !== seat.id));
      return;
    }
    if (accommodationIds.length >= passengers) return;
    onAccommodationIdsChange([...accommodationIds, seat.id]);
  };

  const deckGrid = (deck: number) => {
    const deckSeats = seats.filter((seat) => (seat.deck ?? 1) === deck);
    const maxColumn = Math.max(...deckSeats.map((seat) => seat.column ?? 1));
    return (
      <Box key={deck}>
        {decks.length > 1 && (
          <Typography variant="caption" color="text.secondary">
            {t('travelServices.buses.deck', { deck })}
          </Typography>
        )}
        {/* La butaca es un blanco táctil: en mobile la columna crece con el
            botón (el mínimo lo pone el contenedor del modal), si no el asiento
            desbordaría su celda. */}
        <Box
          display="grid"
          gap={0.5}
          justifyContent="start"
          gridTemplateColumns={{
            xs: `repeat(${maxColumn}, ${MOBILE_TAP_TARGET}px)`,
            sm: `repeat(${maxColumn}, 40px)`,
          }}
          mt={0.5}
        >
          {deckSeats.map((seat) => {
            const selected = accommodationIds.includes(seat.id);
            const free = seat.availability !== 0;
            /** El número pelado: "Asiento 61 (Cama)" no entra en una butaca. */
            const label = seat.id;
            return (
              <Tooltip
                key={seat.id}
                title={[
                  seat.name,
                  // Lo que suma el asiento ya lo resolvió el backend (`supplement`).
                  seat.supplement && seat.supplement.final.amountMinor > 0
                    ? t('travelServices.buses.seatPremium', {
                      price: formatMoney(seat.supplement.final),
                    })
                    : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              >
                {/* El span deja el tooltip vivo sobre asientos deshabilitados. */}
                <Box
                  component="span"
                  sx={{ gridRow: seat.row, gridColumn: seat.column }}
                >
                  <ToggleButton
                    value={seat.id}
                    size="small"
                    disabled={!free && !selected}
                    selected={selected}
                    onChange={() => toggleSeat(seat)}
                    sx={{
                      width: { xs: MOBILE_TAP_TARGET, sm: 40 },
                      height: { xs: MOBILE_TAP_TARGET, sm: 36 },
                      p: 0,
                      fontSize: 12,
                    }}
                  >
                    {label}
                  </ToggleButton>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Typography variant="subtitle2">
          {t('travelServices.buses.seatMap')}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          label={t('travelServices.buses.seatsChosen', {
            chosen: accommodationIds.length,
            total: passengers,
          })}
        />
      </Box>

      {/* Sin contexto comercial el backend no cotizó premiums ni cambios de
          calidad: se avisa, no se deja pasar por "sin adicionales". */}
      {options.pricingState === 'unavailable' && (
        <Alert severity="warning">
          {t('travelServices.ferry.pricingUnavailable')}
        </Alert>
      )}

      {/* Cambiar de calidad mirando el mapa: viaja como ticketTypeId. */}
      {options.ticketTypes.length > 1 && (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={ticketTypeId || offerClassCode || ''}
          onChange={(_event, next: string | null) => {
            if (next !== null) {
              // La calidad de la propia oferta no se manda: es el default.
              onTicketTypeChange(next === offerClassCode ? '' : next);
            }
          }}
        >
          {/* Lo que cambia la calidad respecto de la oferta ya lo resolvió el
              backend (`supplement.final`, con markup); el `price` crudo del
              operador no se pinta. */}
          {options.ticketTypes.map((type) => (
            <ToggleButton key={type.id} value={type.id}>
              {type.name}
              {type.supplement && type.supplement.final.amountMinor !== 0
                ? ` · ${type.supplement.final.amountMinor > 0 ? '+ ' : ''}${formatMoney(type.supplement.final)}`
                : ''}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}

      {/* Una unidad ancha scrollea DENTRO de su caja: la pantalla no se
          desplaza de costado por un mapa de asientos. */}
      <Box display="flex" gap={4} flexWrap="wrap" sx={{ overflowX: 'auto' }}>
        {decks.map(deckGrid)}
      </Box>

      <Typography variant="caption" color="text.secondary">
        {t('travelServices.buses.seatMapOptional')}
      </Typography>
    </Box>
  );
}
